import { dinero, toSnapshot, type Dinero } from 'dinero.js';
import * as currencies from 'dinero.js/currencies';
import * as z from 'zod';
import {
  currencyCodeSchema,
  type CurrencyCode,
  type SupportedCurrencyCode,
  supportedCurrencyCodeSchema,
} from './currencies.ts';

export {
  currencyCodeByCountryCode,
  currencyCodeSchema,
  type CurrencyCode,
  currencyCodes,
  supportedCurrencyCodeSchema,
  type SupportedCurrencyCode,
  supportedCurrencyCodes,
} from './currencies.ts';

const currencyCatalog = Object.values(currencies);
const currencyByCode = new Map(currencyCatalog.map(currency => [currency.code, currency]));

const getDineroCurrency = (currencyCode: CurrencyCode) => {
  const currency = currencyByCode.get(currencyCode);

  if (!currency) {
    throw new Error(`Unsupported currency code: ${currencyCode}`);
  }

  return currency;
};

export const getCurrencyMinorUnitExponent = (currencyCode: CurrencyCode) =>
  getDineroCurrency(currencyCode).exponent;

export const USD_CURRENCY_CODE = supportedCurrencyCodeSchema.parse('USD');

export const toSupportedCurrencyCode = (
  currencyCode: string | null | undefined,
): SupportedCurrencyCode => {
  const parsed = supportedCurrencyCodeSchema.safeParse(currencyCode);

  return parsed.success ? parsed.data : USD_CURRENCY_CODE;
};

export const moneyJsonSchema = z.object({
  amount: z.number().int(),
  currencyCode: currencyCodeSchema,
});

export type MoneyJson = z.infer<typeof moneyJsonSchema>;
export type Money = Dinero<number>;

export const toMoneyJson = (amount: number, currencyCode: CurrencyCode): MoneyJson =>
  moneyJsonSchema.parse({ amount, currencyCode });

export const moneyFromJson = (value: unknown): Money => {
  const parsed = moneyJsonSchema.parse(value);

  return dinero({
    amount: parsed.amount,
    currency: getDineroCurrency(parsed.currencyCode),
  });
};

export const moneyToJson = (value: Money): MoneyJson => {
  const snapshot = toSnapshot(value);

  return moneyJsonSchema.parse({
    amount: snapshot.amount,
    currencyCode: snapshot.currency.code,
  });
};
